<script setup lang="ts">
import { onMounted, ref } from 'vue';
import type { SelectOption } from 'naive-ui';
import { searchPlaybook } from '@/api';
import { PlaybooksService } from '@/services/playbooks/playbooksService';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import GaiaRemoteSelect, {
  type GaiaRemotePage,
} from '@/components/design/base/selects/GaiaRemoteSelect.vue';

const props = defineProps<{
  playbookUid: string | null;
}>();

const emit = defineEmits<{
  'update:playbookUid': [uid: string | null];
}>();

async function fetchPlaybooks(text: string): Promise<GaiaRemotePage> {
  const response = await searchPlaybook({
    body: { text, limit: 50, column_filters: { status: ['active'] } },
  });
  if (response.error)
    throw new Error(extractErrorDetail(response.error, 'Failed to search playbooks'));
  return {
    options: response.data.results.map(playbook => ({
      label: playbook.name,
      value: playbook.uid,
    })),
  };
}

function handleSearchError(error: unknown): void {
  logger.error('Error searching playbooks:', error);
}

/**
 * The configured playbook may be inactive, and the search only returns active
 * ones — so its label can never arrive in a result. Looked up by id and handed
 * to the field as a seed, which keeps it in the list while it is selected.
 */
const seedOptions = ref<SelectOption[]>([]);

onMounted(async () => {
  if (!props.playbookUid) return;
  try {
    const playbook = await PlaybooksService.getById(props.playbookUid);
    seedOptions.value = [{ label: playbook.name, value: playbook.uid }];
  } catch {
    seedOptions.value = [];
  }
});
</script>

<template>
  <n-form label-placement="top" style="max-width: 420px; padding: 12px">
    <n-form-item label="Playbook">
      <gaia-remote-select
        :value="playbookUid"
        clearable
        placeholder="Search playbooks"
        :debounce="400"
        :fetch="fetchPlaybooks"
        :seed-options="seedOptions"
        @update:value="(uid: string | null) => emit('update:playbookUid', uid)"
        @error="handleSearchError"
      />
    </n-form-item>
  </n-form>
</template>
